import PropTypes from 'prop-types';
import React from 'react';
import { Link, withRouter } from 'react-router';
import classnames from 'classnames';

import { formatNumber } from '../../utils/formats';

import {
  loadMore,
  paginationItem,
  active,
  smallTopPadding as smallTopPaddingStyle,
  smallBottomPadding as smallBottomPaddingStyle,
} from './styles.module.css';

const getPagePath = (pathname, pageNumber) => {
  let path = '';

  if (pathname.includes('page')) {
    path =
      pageNumber === 1
        ? pathname.replace(/page=[0-9]+/, '')
        : pathname.replace(/page=[0-9]+/, `page=${pageNumber}`);
  } else {
    path =
      pageNumber === 1
        ? pathname
        : `${pathname}${pathname.includes('?') ? '&' : '?'}page=${pageNumber}`;
  }

  if (path.endsWith('?')) {
    path = path.substring(0, path.length - 1);
  }

  return path;
};
const getUrlwithSearchParams = (location) => {
  if (!location) return null;
  return `${location.pathname}${location.search}`;
};
const PaginationItem = ({
  pageNumber,
  pathname,
  currentPageNumber,
  onClick,
  preventRedirect,
}) => {
  const linkProps =
    !preventRedirect && pathname
      ? {
          to: getPagePath(pathname, pageNumber),
        }
      : // Do not show the href if redirection is not allowed.
        undefined;

  return (
    <Link
      className={`${paginationItem} ${
        currentPageNumber === pageNumber ? active : ''
      }`}
      onClick={() => {
        onClick(pageNumber);
      }}
      {...linkProps}
    >
      <span>{formatNumber(pageNumber)}</span>
    </Link>
  );
};

const EllipsisItem = () => (
  <div className={paginationItem}>
    <span>{'…'}</span>
  </div>
);

const LeftArrow = ({
  currentPageNumber,
  pathname,
  onClick,
  preventRedirect,
}) => {
  const prevPage = currentPageNumber - 1 > 0 ? currentPageNumber - 1 : null;
  return (
    <Link
      className={paginationItem}
      onClick={(e) => {
        if (preventRedirect) {
          e.preventDefault();
        }
        if (prevPage && onClick) {
          onClick(prevPage);
        }
      }}
      {
        // Do not show the href is redirection is not allowed.
        ...(!preventRedirect &&
          prevPage &&
          pathname && {
            to: getPagePath(pathname, prevPage),
            rel: 'prev',
          })
      }
    >
      <img
        src={`${MEDIA_ROOT}/icons/icn_arrowleft_circle.svg`}
        alt="Previous"
      />
    </Link>
  );
};

const RightArrow = ({
  currentPageNumber,
  totalPages,
  pathname,
  onClick,
  preventRedirect,
}) => {
  const nextPage =
    currentPageNumber + 1 <= totalPages ? currentPageNumber + 1 : null;
  return (
    <Link
      className={paginationItem}
      onClick={(e) => {
        if (preventRedirect) {
          e.preventDefault();
        }
        if (nextPage && onClick) {
          onClick(nextPage);
        }
      }}
      {
        // Do not show the href is redirection is not allowed.
        ...(!preventRedirect &&
          nextPage &&
          pathname && {
            to: getPagePath(pathname, nextPage),
            rel: 'next',
          })
      }
    >
      <img src={`${MEDIA_ROOT}/icons/icn_arrowright_circle.svg`} alt="Next" />
    </Link>
  );
};

const Pagination = (props) => {
  const {
    location,
    onClick,
    smallTopPadding,
    smallBottomPadding,
    preventRedirect,
    maxElements,
    total: totalUntyped,
    currentPageNumber: currentPageUntyped,
    perPage: perPageUntyped,
  } = props;
  /*
  There is a risk of passing a string, just safe guard types to make it more
  predictable on runtime
   */
  const total = Number(totalUntyped);
  const currentPage = Number(currentPageUntyped);
  const perPage = Number(perPageUntyped);

  if (isNaN(total) || isNaN(currentPage) || isNaN(perPage)) {
    console.error('Pagination prop is NaN');
    return null;
  }

  const totalPages = Math.ceil(total / perPage);
  const elementsCount = Math.min(totalPages, maxElements);
  const currentPageNumber = parseInt(currentPage, 10);
  if (totalPages === 1) return null;

  const styleClassNames = classnames({
    [loadMore]: true,
    [smallTopPaddingStyle]: smallTopPadding,
    [smallBottomPaddingStyle]: smallBottomPadding,
  });
  const linkProps = {
    pathname: getUrlwithSearchParams(location),
    currentPageNumber,
    onClick,
    totalPages,
    preventRedirect,
  };

  if (totalPages >= maxElements) {
    if (currentPageNumber > 3 && currentPageNumber < totalPages - 2) {
      return (
        <div className={styleClassNames}>
          <LeftArrow {...linkProps} />
          <PaginationItem pageNumber={1} {...linkProps} />
          <EllipsisItem />
          <PaginationItem pageNumber={currentPageNumber} {...linkProps} />
          <EllipsisItem />
          <PaginationItem pageNumber={totalPages} {...linkProps} />
          <RightArrow {...linkProps} />
        </div>
      );
    } else if (currentPageNumber <= 3) {
      return (
        <div className={styleClassNames}>
          <LeftArrow {...linkProps} />
          {[...Array(3)].map((e, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <PaginationItem {...linkProps} key={i} pageNumber={i + 1} />
          ))}
          <EllipsisItem />
          <PaginationItem {...linkProps} pageNumber={totalPages} />
          <RightArrow {...linkProps} />
        </div>
      );
    }
    return (
      <div className={styleClassNames}>
        <LeftArrow {...linkProps} />
        <PaginationItem {...linkProps} pageNumber={1} />
        <EllipsisItem />
        {[...Array(3)].map((e, i) => (
          <PaginationItem
            {...linkProps}
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            pageNumber={totalPages - 2 + i}
          />
        ))}
        <RightArrow {...linkProps} />
      </div>
    );
  }
  return (
    <div className={styleClassNames}>
      <LeftArrow {...linkProps} />
      {[...Array(elementsCount)].map((e, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <PaginationItem {...linkProps} key={i} pageNumber={i + 1} />
      ))}
      <RightArrow {...linkProps} />
    </div>
  );
};

export default withRouter(Pagination);

Pagination.propTypes = {
  /**
   * Function to invoke upon clicking 'Load More'.
   */
  onClick: PropTypes.func,
  /**
   * Number of events for current list (and query) found on the server.
   */
  total: PropTypes.number.isRequired,

  preventRedirect: PropTypes.bool,
};

Pagination.defaultProps = {
  onClick: undefined,
  total: 0,
  maxElements: 5,
  perPage: 12,
  currentPageNumber: 1,
  preventRedirect: true,
};
